import { z } from 'zod';
import { ROLES, TASK_KINDS } from '@bulk/types';
import { dateOnlySchema, idSchema, patchOf, timeHHMM } from './common';

const localizedNames = z.record(z.string(), z.string().trim().max(120));
const localizedTexts = z.record(z.string(), z.string().trim().max(300));
const weekdaySchema = z.number().int().min(1).max(7);

export const TASK_DAYS_MAX = 62;

export const taskShiftSchema = z.object({
  id: idSchema,
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  startTime: z.string(),
  endTime: z.string(),
});
export type TaskShiftDto = z.infer<typeof taskShiftSchema>;

export const taskListItemInputSchema = z.object({
  id: idSchema.optional().describe('An item the list already has, to keep it; omit for a new one'),
  text: z.string().trim().min(1).max(300),
  textLocalized: localizedTexts.default({}),
});
export type TaskListItemInput = z.infer<typeof taskListItemInputSchema>;

export const createTaskListSchema = z.object({
  locationId: idSchema.describe('The branch that keeps this list'),
  name: z.string().trim().min(1).max(80),
  nameLocalized: localizedNames.default({}),
  shiftDefinitionId: idSchema.nullable().optional().describe('The shift it belongs to; at most one of this and dueTime'),
  dueTime: timeHHMM.nullable().optional().describe('The time of day it is due, on the gym’s clock'),
  weekdays: z.array(weekdaySchema).max(7).default([]).describe('ISO weekdays (Monday 1 to Sunday 7) it runs on; none means every day'),
  active: z.boolean().default(true),
  items: z.array(taskListItemInputSchema).min(1).max(60),
});
export type CreateTaskListInput = z.infer<typeof createTaskListSchema>;

export const updateTaskListSchema = patchOf(createTaskListSchema.omit({ locationId: true }));
export type UpdateTaskListInput = z.infer<typeof updateTaskListSchema>;

export const taskListItemSchema = z.object({
  id: idSchema,
  text: z.string(),
  textLocalized: z.record(z.string(), z.string()),
  sortOrder: z.number().int(),
});

export const taskListSchema = z.object({
  id: idSchema,
  locationId: idSchema,
  locationName: z.string(),
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  shift: taskShiftSchema.nullable(),
  dueTime: z.string().nullable(),
  weekdays: z.array(weekdaySchema),
  active: z.boolean(),
  sortOrder: z.number().int(),
  items: z.array(taskListItemSchema),
});
export type TaskListDto = z.infer<typeof taskListSchema>;

export const taskItemSchema = z.object({
  id: idSchema,
  text: z.string(),
  textLocalized: z.record(z.string(), z.string()),
  doneAt: z.string().nullable(),
  doneBy: z.string().nullable().describe('Who ticked it; null while it is open'),
  doneByUserId: idSchema.nullable(),
  note: z.string().nullable(),
  photoUrl: z.string().nullable().describe('A signed link that expires after a few minutes'),
});
export type TaskItemDto = z.infer<typeof taskItemSchema>;

export const taskInstanceSchema = z.object({
  id: idSchema,
  kind: z.enum(TASK_KINDS),
  date: z.string(),
  locationId: idSchema,
  locationName: z.string(),
  listId: idSchema.nullable(),
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  notes: z.string().nullable(),
  shift: taskShiftSchema.nullable(),
  dueTime: z.string().nullable(),
  assignee: z.object({ id: idSchema, name: z.string() }).nullable().describe('For a one-off given to a person'),
  itemsTotal: z.number().int(),
  itemsDone: z.number().int(),
  completedAt: z.string().nullable().describe('When the last item was ticked, once all are'),
  items: z.array(taskItemSchema),
});
export type TaskInstanceDto = z.infer<typeof taskInstanceSchema>;

export const taskDaySchema = z.object({
  date: z.string(),
  today: z.string().describe('The gym’s current day'),
  instances: z.array(taskInstanceSchema),
});
export type TaskDayDto = z.infer<typeof taskDaySchema>;

export const taskDayQuerySchema = z.object({ date: dateOnlySchema.optional().describe('The gym’s day; today when omitted') });
export type TaskDayQuery = z.infer<typeof taskDayQuerySchema>;

export const taskHistoryQuerySchema = z.object({ from: dateOnlySchema, to: dateOnlySchema });
export type TaskHistoryQuery = z.infer<typeof taskHistoryQuerySchema>;

export const taskHistorySchema = z.array(
  z.object({
    date: z.string(),
    locationId: idSchema,
    locationName: z.string(),
    instances: z.number().int(),
    itemsTotal: z.number().int(),
    itemsDone: z.number().int(),
  }),
);
export type TaskHistoryDto = z.infer<typeof taskHistorySchema>;

export const taskListQuerySchema = z.object({ includeInactive: z.stringbool().default(true) });
export type TaskListQuery = z.infer<typeof taskListQuerySchema>;

export const createOneOffTaskSchema = z.object({
  locationId: idSchema,
  date: dateOnlySchema.describe('The gym’s day; today or later'),
  text: z.string().trim().min(1).max(300),
  textLocalized: localizedTexts.default({}),
  notes: z.string().trim().max(1000).nullable().optional(),
  dueTime: timeHHMM.nullable().optional(),
  assignedGymUserId: idSchema.nullable().optional().describe('A person from `GET /tasks/assignees`; at most one of this and shiftDefinitionId'),
  shiftDefinitionId: idSchema.nullable().optional().describe('Or a shift, so whoever works it that day sees the task'),
});
export type CreateOneOffTaskInput = z.infer<typeof createOneOffTaskSchema>;

export const tickTaskItemSchema = z.object({ note: z.string().trim().max(500).nullable().optional() });
export type TickTaskItemInput = z.infer<typeof tickTaskItemSchema>;

export const taskAssigneesQuerySchema = z.object({ locationId: idSchema });
export type TaskAssigneesQuery = z.infer<typeof taskAssigneesQuerySchema>;

export const taskAssigneeSchema = z.object({
  id: idSchema,
  name: z.string(),
  role: z.enum(ROLES),
});
export type TaskAssigneeDto = z.infer<typeof taskAssigneeSchema>;
